import { toast } from 'sonner';
import { ClipboardCheck, RefreshCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { useApplyTemplate } from '../../hooks/onboardingOps';
import { ApiError } from '../../lib/api';

/**
 * Apply the person's role template on demand. Auto-assign only runs at
 * creation time keyed on position, so this covers people who predate a
 * template or whose position changed. Idempotent: already-assigned items
 * are skipped.
 */
export function ApplyTemplatePanel({
  personId,
  position,
}: {
  personId: string;
  position: string | null;
}) {
  const apply = useApplyTemplate(personId);

  async function run() {
    try {
      const r = await apply.mutateAsync();
      if (r.skipped === 'no_position') {
        toast.error('Set a position first, then apply the template.');
      } else if (r.skipped === 'no_template') {
        toast.error(`No onboarding template exists for "${position}".`);
      } else if (r.skipped === 'no_programme') {
        toast.error('No onboarding programme is configured.');
      } else if (r.assigned === 0) {
        toast.success('Already up to date, nothing new to assign.');
      } else {
        toast.success(`Assigned ${r.assigned} item${r.assigned === 1 ? '' : 's'} from the template.`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not apply template');
    }
  }

  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line/10 bg-surface px-5 py-4 shadow-sm">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-fg-muted" />
          <h2 className="text-sm font-semibold">Apply role template</h2>
        </div>
        <p className="mt-1 text-xs text-fg-muted">
          Assigns the onboarding items mapped to{' '}
          <strong>{position ?? 'this person’s position'}</strong>. Safe to
          run repeatedly; existing items are skipped.
        </p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        iconLeft={<RefreshCcw className="h-4 w-4" />}
        loading={apply.isPending}
        onClick={run}
      >
        Apply template
      </Button>
    </section>
  );
}
